import {Injectable} from '@angular/core';
import {AuthService} from '../../../auth/services/auth/auth.service';
import {AppService} from '../../../app-common/services/app/app.service';
import {RequestsWebsocketService} from '../../../app-common/services/server/requests-websocket.service';
import {GetAccountPermissionsResponse} from '../../data/responses/getAccountPermissionsResponse';
import {EditGroupPermissionsRequest} from '../../data/requests/editGroupPermissionsRequest';
import {EditGroupPermissionOperation} from '../../data/editGroupPermissionOperation';
import {Permission} from '../../data/permission';
import {Group} from '../../data/group';
import {GroupPermission} from '../../data/groupPermission';
import {GroupUser} from '../../data/groupUser';
import {PleaseWaitService} from '../../../app-common/services/please-wait/please-wait.service';
import {AddGroupRequest} from '../../data/requests/addGroupRequest';
import {Observable, Subject} from 'rxjs';
import {BaseEntityEnum, CrudOperationEnum} from '../../../activity-log/data/operationTypeEnum';
import {ApiService} from '../../../app-common/services/server/api.service';
import {LoggerService} from '../../../logging/services/logger.service';
import {LogLevel} from '../../../logging/data/logLevel';
import {LogSourceType} from '../../../logging/data/logSourceType';
import {LogSource} from '../../../logging/data/logSource';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  permissions: Permission[];
  groupId: number;
  loaded = false;

  private refreshWorkerList = new Subject<void>();

  constructor(public authService: AuthService,
              public appService: AppService,
              public httpService: ApiService,
              public pleaseWaitService: PleaseWaitService,
              public websocket: RequestsWebsocketService,
              private loggingService: LoggerService) {
    this.authService = authService;
    this.appService = appService;
    this.websocket = websocket;
  }


  triggerRefreshWorkerList(): void {
    this.refreshWorkerList.next();
  }

  listenRefreshWorkerList(): Observable<void> {
    return this.refreshWorkerList.asObservable();
  }

  async getAllPermissions() {
    const response = await this.httpService.applicationApiClient.get<Permission[]>('Permission/Permissions');
    return response;
  }

  async getMyPermissions() {
    while (!this.authService.isLoggedIn) {
      await this.sleep(10);
    }
    const response = await this.httpService.applicationApiClient.get<GetAccountPermissionsResponse>('Permission/MyPermissions');
    this.permissions = response.permissions;
    this.groupId = response.groupId;
    this.loaded = true;

    return response;
  }

  async getGroups() {
    const response = await this.httpService.applicationApiClient.get<Group[]>('Permission/Groups');
    return response;
  }

  async getGroupPermissions(groupId: number) {
    const response = await this.httpService.applicationApiClient.get<GroupPermission[]>(`Permission/Group/${groupId}/Permissions`);
    return response;
  }

  async getGroupUsers(groupId: number) {
    const response = await this.httpService.applicationApiClient.get<GroupUser[]>(`Permission/Group/${groupId}/Accounts`);
    return response;
  }

  async editGroupPermissions(groupId: number, operations: EditGroupPermissionOperation[]) {
    const request = new EditGroupPermissionsRequest();
    request.operations = operations;
    this.pleaseWaitService.addToQueue();

    let response;
    try {
      response = await this.httpService.applicationApiClient.put('Permission/Group', groupId, request);
      this.appService.showSuccessMessage('Group Permissions Edited');
      this.pleaseWaitService.removeFromQueue();
      this.loggingService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.PermissionService, 'Modified group with Id ' + groupId);
      return true;
    } catch (ex) {
      this.loggingService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.PermissionService, ex);
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }

  async addGroup(name: string) {
    const request = new AddGroupRequest();
    request.groupName = name;

    let response;
    try {
      response = await this.httpService.applicationApiClient.post('Permission/Groups', request);
      this.appService.showSuccessMessage('Group Added');
      this.pleaseWaitService.removeFromQueue();
      this.loggingService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.PermissionService, 'Added group ' + name);
      return true;
    } catch (ex) {
      this.loggingService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.PermissionService, ex);
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }

  async hasPermission(entity: BaseEntityEnum, operation: CrudOperationEnum) {
    const permission = `${BaseEntityEnum[entity]}_${CrudOperationEnum[operation]}`;
    if (!this.loaded) {
      await this.getMyPermissions();
    }
    for (const perm of this.permissions) {
      if (perm.identifier === permission) {
        return true;
      }
    }
    return false;
  }

  async sleep(milliseconds: number) {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
  }
}
